import { authStore } from '@/core/auth/authStore';
import { env } from '@/core/config/env';
import type {
  AuthBootstrapData,
  LoginRequest,
  MeResponse,
  PermissionGrant,
  TenantInfo,
  TokenPairResponse,
} from '@/core/types/auth';
import { mockAuthApi } from '@/mock/mockAdapter';
import { httpClient } from './httpClient';

const requireAccessToken = (): string => {
  const token = authStore.getState().accessToken;
  if (!token) throw new Error('Access token is missing.');
  return token;
};

export const authApi = {
  async login(request: LoginRequest): Promise<TokenPairResponse> {
    if (env.isDemo) return mockAuthApi.login(request);
    const response = await httpClient.post<TokenPairResponse>(
      '/api/v1/auth/password/login',
      request,
      { authenticated: false, retryOnUnauthorized: false },
    );
    return response.data;
  },

  async logout(): Promise<void> {
    if (env.isDemo) return mockAuthApi.logout();
    await httpClient.post<void>('/api/v1/auth/logout', undefined, {
      retryOnUnauthorized: false,
    });
  },

  async bootstrap(): Promise<AuthBootstrapData> {
    const accessToken = requireAccessToken();
    if (env.isDemo) {
      const [me, tenant, permissions] = await Promise.all([
        mockAuthApi.getMe(accessToken),
        mockAuthApi.getTenant(accessToken),
        mockAuthApi.getPermissions(accessToken),
      ]);
      return { me, tenant, permissions };
    }

    const [me, tenant, permissions] = await Promise.all([
      httpClient.get<MeResponse>('/api/v1/me'),
      httpClient.get<TenantInfo>('/api/v1/tenants/current'),
      httpClient.get<PermissionGrant[]>('/api/v1/me/permissions'),
    ]);

    return { me: me.data, tenant: tenant.data, permissions: permissions.data };
  },
};
